import React, { useState, useEffect, useRef } from 'react';
import propTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { useDispatch, useSelector } from 'react-redux';
import { getDateFromHours } from 'helpers/_date-helpers';
import PageLoader from 'components/PageLoader';
import AlertModal from 'components/AlertModal';
import SheetDetails from './components/SheetDetails';
import styles from './styles';
import { worklogsActions, postNewWorklogSheet } from 'store/worklogs';
import { updateSheets } from 'store/schedule/schedulePageOperation';
import { isSelectedTimeValid, generateHoursObject, isStartTimeEarlierOnJob } from 'helpers/helpers';
import { fetchProjects } from 'store/projectTypes/projectTypesOperations';
import { fetchAllContractorsData } from 'store/contractors/contractorsPageOperations';
import { parse, startOfDay, format, add, startOfWeek, endOfWeek, subDays, addDays, differenceInDays } from 'date-fns';
import { checkUserRole } from 'helpers/_helpers';
import { SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import {
  globalBEDateFormat,
  DAY_PART,
  convertTo24HourFormat,
  convertTimeObject,
} from 'common/dateFormatConfig';
import { useWeekStart } from 'hooks/useWeekStart';
import { parseStringDate } from 'helpers/_date-helpers';

const JOB_TIME_ERROR = 'Start hours should be earlier than end hours';

const validation = (values, getTimeFormatHook, type, sheet) => {
  const errors = {};
  const isUpdate = type === 'update';
  const hoursValues = {
    dayStart: values.dayStart,
    hourStart: values.hourStart,
    minuteStart: values.minuteStart,
    amPmStart: values.amPmStart,
    dayEnd: values.dayEnd,
    hourEnd: values.hourEnd,
    minuteEnd: values.minuteEnd,
    amPmEnd: values.amPmEnd,
  };
  const { isValid, errorsOnFields: timeErrors } = isSelectedTimeValid(hoursValues, getTimeFormatHook);
  if (!isValid) {
    errors.dayStart = timeErrors.includes('dayStart');
    errors.hourStart = timeErrors.includes('hourStart');
    errors.minuteStart = timeErrors.includes('minuteStart');
    errors.amPmStart = timeErrors.includes('amPmStart');
    errors.dayEnd = timeErrors.includes('dayEnd');
    errors.hourEnd = timeErrors.includes('hourEnd');
    errors.minuteEnd = timeErrors.includes('minuteEnd');
    errors.amPmEnd = timeErrors.includes('amPmEnd');
  }

  // Update mode stores on-job start under {day,hour,minute,amPm}Start; map onto onJob* for the shared validator.
  const startTimeOnJobValues = isUpdate
    ? {
        ...values,
        onJobDay: values.dayStart,
        onJobHours: values.hourStart,
        onJobMinutes: values.minuteStart,
        onJobDayPart: values.amPmStart,
      }
    : values;
  const { errorsOnFields: startTimeOnJobError } = isStartTimeEarlierOnJob(startTimeOnJobValues, getTimeFormatHook, sheet);
  if (Object.getOwnPropertyNames(startTimeOnJobError)?.length) {
    if (isUpdate) {
      errors.hourStart = startTimeOnJobError.onJobHours;
      errors.minuteStart = startTimeOnJobError.onJobMinutes;
      errors.amPmStart = startTimeOnJobError.onJobDayPart;
    } else {
      errors.onJobHours = startTimeOnJobError.onJobHours;
      errors.onJobMinutes = startTimeOnJobError.onJobMinutes;
      errors.onJobDayPart = startTimeOnJobError.onJobDayPart;
    }
    errors.startTimeHours = startTimeOnJobError.startTimeHours;
    errors.startTimeMinutes = startTimeOnJobError.startTimeMinutes;
    errors.startTimeDayPart = startTimeOnJobError.startTimeDayPart;
  }

  if (
    type === 'create' &&
    !values.travelTimeOnly &&
    !+values.estimatedHours &&
    !+values.estimatedMinutes
  ) {
    errors.estimatedHours = JOB_TIME_ERROR;
  }

  if (!values.contractorId?.trim()) {
    errors.contractorId = "This field can't be blank";
  }
  if (!values.projectId?.trim()) {
    errors.projectId = "This field can't be blank";
  }
  if (values.notes.trim() && values.notes.trim().length > 150) {
    errors.notes = 'You riched the limit of symbols. Max is 150';
  }
  if (values.schedulerNotes.trim() && values.schedulerNotes.trim().length > 150) {
    errors.schedulerNotes = 'You riched the limit of symbols. Max is 150';
  }

  if (values.forceAccountHours.trim()) {
    if (!/^[0-9]+$/i.test(values.forceAccountHours)) {
      errors.forceAccountHours = 'The entry is invalid. The field is numeric only.';
    } else if (+values.forceAccountHours > 48 || +values.forceAccountHours < 0) {
      errors.forceAccountHours = 'Must be a number from 0 to 48';
    }
  }

  if (values.humidity.trim()) {
    if (!/^[0-9]+$/i.test(values.humidity)) {
      errors.humidity = 'The entry is invalid. The field is numeric only.';
    } else if (+values.humidity > 100 || +values.humidity < 0) {
      errors.humidity = 'Must be a number from 0 to 100';
    }
  }

  if (values.weatherNotes.trim() && values.weatherNotes.trim().length > 50) {
    errors.weatherNotes = 'You riched the limit of symbols. Max is 50';
  }

  if (values.travelTimeToHours.trim() && !/^[0-9]+$/i.test(values.travelTimeToHours)) {
    errors.travelTimeToHours = 'The entry is invalid. The field is numeric only.';
  } else if (+values.travelTimeToHours > 24 || +values.travelTimeToHours < 0) {
    errors.travelTimeToHours = 'Must be a number from 0 to 24';
  }

  if (values.travelTimeToMinutes.trim() && !/^[0-9]+$/i.test(values.travelTimeToMinutes)) {
    errors.travelTimeToMinutes = 'The entry is invalid. The field is numeric only.';
  } else if (+values.travelTimeToMinutes > 59 || +values.travelTimeToMinutes < 0) {
    errors.travelTimeToMinutes = 'Must be a number from 0 to 59';
  }

  //* travelTimeFrom

  if (values.travelTimeFromHours.trim() && !/^[0-9]+$/i.test(values.travelTimeFromHours)) {
    errors.travelTimeFromHours = 'The entry is invalid. The field is numeric only.';
  } else if (+values.travelTimeFromHours > 24 || +values.travelTimeFromHours < 0) {
    errors.travelTimeFromHours = 'Must be a number from 0 From 24';
  }

  if (values.travelTimeFromMinutes.trim() && !/^[0-9]+$/i.test(values.travelTimeFromMinutes)) {
    errors.travelTimeFromMinutes = 'The entry is invalid. The field is numeric only.';
  } else if (+values.travelTimeFromMinutes > 59 || +values.travelTimeFromMinutes < 0) {
    errors.travelTimeFromMinutes = 'Must be a number from 0 to 59';
  }

  //* rainPayHours
  if (values.rainPayHours.trim() && !/^[0-9]+$/i.test(values.rainPayHours)) {
    errors.rainPayHours = 'The entry is invalid. The field is numeric only.';
  } else if (
    values.rainPayHours.trim() &&
    (+values.rainPayHours > 24 || +values.rainPayHours < 0)
  ) {
    errors.rainPayHours = 'Must be a number from 0 From 24';
  }

  if (values.rainPayMinutes.trim() && !/^[0-9]+$/i.test(values.rainPayMinutes)) {
    errors.rainPayMinutes = 'The entry is invalid. The field is numeric only.';
  } else if (
    values.rainPayMinutes.trim() &&
    (+values.rainPayMinutes > 59 || +values.rainPayMinutes < 0)
  ) {
    errors.rainPayMinutes = 'Must be a number from 0 to 59';
  }

  return errors;
};

const SheetForm = ({
  sheet,
  type,
  classes,
  closeDrawer,
  openSnackbar,
  defaultDate,
  isDataLoading,
}) => {
  const settings = useSelector((store) => store.personalProfile?.organization?.settings);
  const activeTenantLocation = useSelector((store) => store.personalProfile.organizationLocation);
  const { weekDayFromO } = useWeekStart();
  const { dateFormat } = useDateSettingsFormat();
  const getTimeFormatHook = useTimeSettingsFormat();
  const is12Format = getTimeFormatHook().is12Format;
  const [contractors, setContractors] = useState([]);
  const [contractorAlternateContacts, setContractorAlternateContacts] = useState([]); //fully obj of contact
  const [projects, setProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    text: '',
  });
  const [locationEditor, setLocationEditor] = useState(false);
  const [rallyPointEditor, setRallyPointEditor] = useState(false);
  const [tenantLocationId, setTenantLocationId] = useState(sheet?.tenantLocationId || activeTenantLocation?._id);

  const formikRef = useRef();
  const dispatch = useDispatch();

  const allProjects = useSelector((store) => store.projectTypes.projects.data);
  const projectsLoading = useSelector((store) => store.projectTypes.projects.isLoading);
  const allContractors = useSelector((store) => store.contractors.data);
  const contractorsLoading = useSelector((store) => store.contractors.isLoading);
  const { scheduleView } = useSelector((store) => store.schedule);

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      fetchProjects({
        filters: { status: 'Active' },
        page: NaN,
      }, {
        create: type === 'create',
        id: type === 'update' && sheet._id,
        tenantLocationId: tenantLocationId
      })
    );
    dispatch(
      fetchAllContractorsData({
        create: type === 'create',
        id: type === 'update' && sheet._id,
        tenantLocationId: tenantLocationId
      })
    );
    if (formikRef?.current) {
      formikRef?.current?.resetForm({ values: initialCreateValues });
    }
  }, [tenantLocationId]);

  useEffect(() => {
    if (!settings) return;
    formikRef?.current?.resetForm({ values: initialCreateValues });
  }, [settings]);

  useEffect(() => {
    const contractors = allContractors;

    if (type === 'update') {
      const contractorId = sheet.project.contractor.oldContractorId || sheet.contractorId;

      const projects = allProjects.find((project) => project.contractorId === contractorId);

      const locations = sheet.project.locations || [];

      setContractors(contractors);
      setProjects(projects);
      setLocations(locations);
      setIsLoading(false);
    } else {
      setContractors(contractors);
      setProjects(projects);
      setIsLoading(false);
    }
  }, [allProjects, allContractors]);


  const handleSelectSearchProject = (setFieldValue) => (projectId) => {
    const id = projectId ?? '';
    const contractorId = allProjects.find((project) => project._id === id)?.contractorId || '';
    // contractor part
    setFieldValue('searchProject', id, false);
    setFieldValue('locations', [''], false);
    setFieldValue('documentStorageId', '', false);
    setFieldValue('contractorId', contractorId, true);
    setDocuments([]);

    const currContractor = allContractors.find((el) => el._id === id);
    if (currContractor) {
      setContractorAlternateContacts(currContractor?.contacts || []);
    }
    if (!contractorId) {
      setProjects([]);
      setContractorAlternateContacts([]);
      setLocations([]);
      return;
    }

    const contractorProjects = allProjects.filter((project) => project.contractorId === contractorId);
    setProjects(contractorProjects);

    // Project part
    setFieldValue('projectId', id, false);
    const project = contractorProjects.find((project) => {
      return project._id === id;
    });
    setLocations([
      ...(project.locations || []).map((el) => ({ ...el, type: 'projectLocation' })),
      ...(project.rallyPoints || []).map((el) => ({ ...el, type: 'rallyPoint' })),
    ]);
    setDocuments(project?.documentStorage || []);

    if (formikRef?.current) {
      setTimeout(() => formikRef.current?.validateForm(), 0);
    }
  }

  const handleSelectContractor = (setFieldValue) => (contractorId) => {
    const id = contractorId ?? '';
    setFieldValue('contractorId', id);
    setFieldValue('locations', ['']);
    setFieldValue('documentStorageId', '');
    setDocuments([]);

    const currContractor = allContractors.find((el) => el._id === id);

    if (currContractor) {
      setContractorAlternateContacts(currContractor?.contacts || []);
    }
    if (!id) {
      setFieldValue('projectId', '');
      setFieldValue('searchProject', '');
      setProjects([]);
      setContractorAlternateContacts([]);
      return;
    }

    const contractorProjects = allProjects.filter((project) => project.contractorId === id);
    setProjects(contractorProjects);
  };

  const handleSelectProject = (setFieldValue) => (projectId) => {
    const id = projectId ?? '';
    setFieldValue('projectId', id);
    setFieldValue('searchProject', '');
    setFieldValue('locations', ['']);

    if (id) {
      const project = projects.find((project) => {
        return project._id === id;
      });
      setLocations([
        ...(project.locations || []).map((el) => ({ ...el, type: 'projectLocation' })),
        ...(project.rallyPoints || []).map((el) => ({ ...el, type: 'rallyPoint' })),
      ]);
      setDocuments(project?.documentStorage || []);
      setFieldValue('documentStorageId', '');
    } else {
      setLocations([]);
    }
  };

  const handleChangeTemperature = (setFieldValue, index, values, arrayHelpers) => (e) => {
    setFieldValue(`temperatures[${index}]`, e.target.value);
    if (values.temperatures.length === index + 1) {
      arrayHelpers.push('');
    }
    if (index >= 0 && e.target.value === '') {
      arrayHelpers.remove(index);
    }
  };

  const handleTemperatureFieldsKeyDown = (e) => {
    // if (!((e.keyCode >= 48 && e.keyCode <= 57) || e.keyCode === 8)) {
    //   e.preventDefault();
    // }
  };

  const getInitialStartTime = (field) => {
    if (!settings?.defaultDayTime) return null;
    const hourObj = convertTimeObject(settings.defaultDayTime, is12Format);
    return hourObj[field];
  };

  const getInitialOnJobTime = (field) => {
    if (!settings?.defaultDayTime) return null;
    let onJobHours = +settings.defaultDayTime.hour + 2;
    let onJobAmPm = settings.defaultDayTime.amPm;
    if (onJobHours === 12) {
      onJobAmPm = onJobAmPm === DAY_PART[0] ? DAY_PART[1] : DAY_PART[0];
    }
    if (onJobHours > 12) {
      onJobHours = onJobHours - 12;
      onJobAmPm = onJobAmPm === DAY_PART[0] ? DAY_PART[1] : DAY_PART[0];
    }
    const result = {
      hour: onJobHours,
      minute: settings.defaultDayTime.minute,
      amPm: onJobAmPm,
    };
    return result[field];
  };

  const handleChangeStartTime = (setValues, values) => ({ hour, minute, amPm }) => {
    const next = {
      ...values,
      startTimeHours: hour,
      startTimeMinutes: minute,
      startTimeDayPart: amPm,
    };
    if (type === 'update') return setValues(next);

    const startHour = is12Format ? convertTo24HourFormat(hour, amPm) : hour;
    const onJob = convertTimeObject({ hour: (+startHour + 2) % 24, minute }, is12Format);
    setValues({
      ...next,
      onJobHours: onJob.hour,
      onJobMinutes: minute,
      onJobDayPart: onJob.amPm,
    });
  };

  const handleChangeShift = (setValues, values) => (e) => {
    const timeOfDay = e.target.value;
    const defaultTime = settings?.[timeOfDay === 'Day' ? 'defaultDayTime' : 'defaultNightTime'];
    if (!defaultTime) return setValues({ ...values, timeOfDay });

    handleChangeStartTime(setValues, { ...values, timeOfDay })(
      convertTimeObject(defaultTime, is12Format)
    );
  };

  const handleSelectLocation = (setFieldValue, sheetLocations, index, type) => (locationId) => {
    const id = locationId ?? '';

    const newSheetLocations = structuredClone(sheetLocations);
    newSheetLocations[index] = id;

    if (type === 'projectLocation') {
      setFieldValue('locations', newSheetLocations);
    } else {
      setFieldValue('rallyPoints', newSheetLocations);
    }
  };

  const handleRemoveLocation = (setFieldValue, sheetLocations, index, type) => () => {
    const newSheetLocations = sheetLocations.slice();
    if (sheetLocations.length <= 1) {
      setFieldValue(type, ['']);
    } else {
      newSheetLocations.splice(index, 1);
      setFieldValue(type, newSheetLocations);
    }
  };

  const handleAppendLocation = (setFieldValue, sheetLocations, type) => () => {
    if (locations.length <= sheetLocations.length) {
      setFieldValue(type, ['']);
    } else {
      setFieldValue(type, [...sheetLocations, '']);
    }
  };

  const getInitialHoursOnUpdate = () => {
    const { hours, createdAt } = sheet;
    return generateHoursObject(hours.start, hours.end, createdAt, getTimeFormatHook);
  };

  const openInfoModal = (text) => {
    setInfoModal({ isOpen: true, text });
  };

  const closeInfoModal = () => {
    setInfoModal({ isOpen: false, text: '' });
  };

  const handleLocationEditor = (value) => {
    if (value === false) {
      setLocationEditor(value);
      return;
    }
    setLocationEditor(value.toString());
  };
  const handleRallyPointEditor = (value) => {
    if (value === false) {
      setRallyPointEditor(value);
      return;
    }
    setRallyPointEditor(value.toString());
  };

  const now = new Date();
  const isFieldTechnician = checkUserRole('Field Technician');
  const calendarStartOfWeek = isFieldTechnician
    ? format(subDays(startOfWeek(now, { weekStartsOn: weekDayFromO }), 1), dateFormat)
    : null;
  const calendarEndOfWeek = isFieldTechnician
    ? format(addDays(endOfWeek(now, { weekStartsOn: weekDayFromO }), 1), dateFormat)
    : null;

  const savedContractorId = sheet
    ? sheet.project.contractor.oldContractorId || sheet.contractorId
    : '';

  const initialCreateValues = {
    contractorId: '',
    projectId: '',
    forceAccount: 'No',
    timeOfDay: 'Day',
    forceAccountHours: '0',
    forceAccountMinutes: '00',
    startDate: defaultDate || new Date(),
    gridDate: defaultDate || new Date(),
    locations: [''],
    rallyPoints: [''],
    alternateContacts: [''],
    notes: '',
    schedulerNotes: '',
    onJobHours: String(Number(getInitialOnJobTime('hour'))) || '2',
    onJobMinutes: getInitialOnJobTime('minute') || '00',
    onJobDayPart: getInitialOnJobTime('amPm') || DAY_PART[0],
    estimatedHours: '2',
    estimatedMinutes: '00',
    startTimeHours: String(Number(getInitialStartTime('hour'))) || '1',
    startTimeMinutes: getInitialStartTime('minute') || '00',
    startTimeDayPart: getInitialStartTime('amPm') || DAY_PART[0],
    travelTimeToHours: '',
    travelTimeToMinutes: '',
    travelTimeFromHours: '',
    travelTimeFromMinutes: '',
    rainPayHours: '',
    rainPayMinutes: '',
    rainPayDescription: '',
    travelTimeOnly: false,
    isRainPayEnabled: false,
    temperatures: [''],
    humidity: '',
    weatherNotes: '',
    documentStorageId: '',
    searchProject: '',
  };

  const initialValues = sheet
    ? {
        // contractorId: Contractors.findOne(savedContractorId).active ? savedContractorId : '',
        contractorId: allContractors.find((contractor) => contractor._id === savedContractorId)
          ? savedContractorId
          : '',
        projectId: sheet.project.oldProjectId || sheet.projectId,
        forceAccount: sheet.forceAccount,
        forceAccountHours: (sheet.forceAccountTime && sheet.forceAccountTime.hours) || '',
        forceAccountMinutes: (sheet.forceAccountTime && sheet.forceAccountTime.minutes) || '00',
        gridDate:
          sheet.grid && sheet.grid.date
            ? parse(sheet.grid.date, dateFormat, new Date())
            : new Date(),
        locations: sheet.locations || [''],
        rallyPoints: sheet.rallyPoints || [''],
        alternateContacts: sheet?.alternateContacts || [],
        notes: sheet.notes || '',
        timeOfDay: sheet.timeOfDay,
        schedulerNotes: sheet.schedulerNotes || '',
        startTimeDay: sheet.startTimeDate
          ? `${differenceInDays(startOfDay(new Date(sheet.startTimeDate)), startOfDay(new Date(sheet.createdAt)))}`
          : '0',
        startTimeHours: sheet.startTime.hourStart,
        startTimeMinutes: sheet.startTime.minuteStart,
        startTimeDayPart: sheet.startTime.amPmStart,
        temperatures: (sheet.weather &&
          sheet.weather.temperature && [...sheet.weather.temperature, '']) || [''],
        humidity: (sheet.weather && sheet.weather.humidity) || '',
        weatherNotes: (sheet.weather && sheet.weather.notes) || '',
        travelTimeToHours: sheet.travelTime ? sheet.travelTime.hours : '',
        travelTimeToMinutes: sheet.travelTime ? sheet.travelTime.minutes : '',
        dayStart: getInitialHoursOnUpdate().dayStart,
        hourStart: getInitialHoursOnUpdate().hourStart,
        minuteStart: getInitialHoursOnUpdate().minuteStart,
        amPmStart: getInitialHoursOnUpdate().amPmStart,
        dayEnd: getInitialHoursOnUpdate().dayEnd,
        hourEnd: getInitialHoursOnUpdate().hourEnd,
        minuteEnd: getInitialHoursOnUpdate().minuteEnd,
        amPmEnd: getInitialHoursOnUpdate().amPmEnd,
        searchProject: sheet.project.oldProjectId || sheet.projectId,
      }
    : initialCreateValues;

  return (
    <div className={classes.formWrapper} role="presentation">
      <Formik
        innerRef={formikRef}
        validate={(values) => validation(values, getTimeFormatHook, type, sheet)}
        validateOnChange={true}
        validateOnBlur={true}
        initialValues={initialValues}
        enableReinitialize={false}
        onSubmit={async (values, { setFieldError, setSubmitting }) => {
          const data = {
            contractorId: values.contractorId,
            projectId: values.projectId,
            locations: values.locations.filter((el) => !!el),
            rallyPoints: values.rallyPoints.filter((el) => !!el),
            addLocations: locations.filter((el) => el.new),
            notes: values.notes,
            schedulerNotes: values.schedulerNotes,
            timeOfDay: values.timeOfDay,
            forceAccount: values.forceAccount,
            primaryContact: values.alternateContacts.filter((loc) => !!loc)[0],
            alternateContacts: values.alternateContacts?.filter((loc) => !!loc)?.slice(1) || [],
            addContacts: contractorAlternateContacts.filter((el) => el.new),
            forceAccountTime:
              values.forceAccount === 'Partial'
                ? {
                    hours: values.forceAccountHours,
                    minutes: values.forceAccountMinutes,
                  }
                : null,
            travelTimeOnly: values.travelTimeOnly,
            isRainPayEnabled: values.isRainPayEnabled,
            rainPay: {
              hours: values.rainPayHours || '0',
              minutes: values.rainPayMinutes || '0',
              description: values.rainPayDescription || '',
            },
            startTime: {
              hour: convertTo24HourFormat(values.startTimeHours, values.startTimeDayPart),
              minute: values.startTimeMinutes,
              // amPmStart: values.startTimeDayPart,
            },
            weather: {
              temperature: values.temperatures,
              humidity: values.humidity,
              notes: values.weatherNotes,
            },
          };
          if (!settings?.miscStipend) {
            delete data.isRainPayEnabled;
            delete data.rainPay;
          }

          if (type === 'create') {
            const createdAt =
              typeof values.startDate === 'string'
                ? startOfDay(parse(values.startDate, dateFormat, new Date()))
                : startOfDay(parseStringDate(values.startDate));
            const gridDate =
              typeof values.gridDate === 'string'
                ? format(parse(values.gridDate, dateFormat, new Date()), globalBEDateFormat)
                : format(parseStringDate(values.gridDate), globalBEDateFormat);
            data.startTimeDate = add(createdAt, {
              hours: convertTo24HourFormat(values.startTimeHours, values.startTimeDayPart),
              minutes: values.startTimeMinutes,
            });
            const start = {
              amPm: values.onJobDayPart,
              hour: values.onJobHours,
              minute: values.onJobMinutes,
            };
            const convertedHours = convertTo24HourFormat(start.hour, start.amPm);
            const tmp = add(createdAt, {
              hours: convertedHours,
              minutes: start.minute,
            });
            data.hours = {
              start: tmp,
              end: add(tmp, {
                hours: values.estimatedHours,
                minutes: values.estimatedMinutes,
              }),
              travelTimeTo: {
                hours: values.travelTimeToHours || '0',
                minutes: values.travelTimeToMinutes || '0',
              },
              travelTimeFrom: {
                hours: values.travelTimeFromHours || '0',
                minutes: values.travelTimeFromMinutes || '0',
              },
            };
            if (values.travelTimeOnly) {
              delete data.hours.start;
              delete data.hours.end;
            }
            data.createdAt = createdAt;
            data.gridDate = gridDate;
            data.grid = {
              date: gridDate,
            };

            try {
              await postNewWorklogSheet(data, { tenantLocationId });
              if (scheduleView !== SCHEDULE_VIEW_TYPES.monthly) {
                dispatch(updateSheets());
                dispatch(worklogsActions.setForceUpdate(true));
              }
              closeDrawer();
            } catch (e) {
              setSubmitting(false);
              openSnackbar('error', e.response.data.message);
            }
          } else {
            const gridDate =
              typeof values.gridDate === 'string'
                ? format(parse(values.gridDate, dateFormat, new Date()), dateFormat)
                : format(values.gridDate, dateFormat);
            const hoursValues = {
              dayStart: values.dayStart,
              hourStart: values.hourStart,
              minuteStart: values.minuteStart,
              amPmStart: values.amPmStart,
              dayEnd: values.dayEnd,
              hourEnd: values.hourEnd,
              minuteEnd: values.minuteEnd,
              amPmEnd: values.amPmEnd,
            };
            const hours = getDateFromHours(hoursValues, sheet.createdAt, getTimeFormatHook);
            data.hours = hours;
            data.grid = {
              date: gridDate,
            };

            // delete contractor and project ids if they aren't changed
            const isOldContractorId =
              data.contractorId === sheet.project.contractor.oldContractorId;
            const isOldProjectId = data.projectId === sheet.project.oldProjectId;
            if (sheet.published || sheet.submittedAt) {
              if (isOldContractorId) delete data.contractorId;
              if (isOldProjectId) delete data.projectId;
            }
          }
        }}
      >
        {({
          errors,
          touched,
          values,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
          setValues,
        }) => (
          <PageLoader loading={isSubmitting || isLoading || projectsLoading || contractorsLoading}>
            <Form autoComplete="off" className={classes.formFlex}>
              <div>
                <Typography variant="h3" style={{ marginBottom: '40px' }}>
                  {type === 'create' ? 'New Work Log' : 'Edit Sheet'}
                </Typography>

                <IconButton
                  className={classes.closeButton}
                  onClick={closeDrawer}
                  aria-label="Close"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>

                <SheetDetails
                  contractors={contractors}
                  projects={projects}
                  allProjects={allProjects}
                  projectLocations={locations}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  handleSelectSearchProject={handleSelectSearchProject}
                  handleSelectContractor={handleSelectContractor}
                  handleSelectProject={handleSelectProject}
                  handleChangeTemperature={handleChangeTemperature}
                  type={type}
                  handleChangeStartTime={handleChangeStartTime}
                  handleChangeShift={handleChangeShift}
                  handleTemperatureFieldsKeyDown={handleTemperatureFieldsKeyDown}
                  sheet={type === 'update' ? sheet : {}}
                  handleSelectLocation={handleSelectLocation}
                  handleRemoveLocation={handleRemoveLocation}
                  handleAppendLocation={handleAppendLocation}
                  locationEditor={locationEditor}
                  rallyPointEditor={rallyPointEditor}
                  handleLocationEditor={handleLocationEditor}
                  handleRallyPointEditor={handleRallyPointEditor}
                  setLocations={setLocations}
                  contractorAlternateContacts={contractorAlternateContacts}
                  setContractorAlternateContacts={setContractorAlternateContacts}
                  documents={documents}
                  calendarStartOfWeek={calendarStartOfWeek}
                  calendarEndOfWeek={calendarEndOfWeek}
                  tenantLocationId={tenantLocationId}
                  setTenantLocationId={setTenantLocationId}
                />
              </div>

              <div className={classes.buttonHolder}>
                <Button
                  color="secondary"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={closeDrawer}
                  disabled={isSubmitting}
                  disableTouchRipple
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="outlined"
                  className={classes.saveButton}
                  disabled={isSubmitting}
                  disableTouchRipple
                >
                  Save
                </Button>
              </div>
            </Form>
          </PageLoader>
        )}
      </Formik>

      {infoModal.isOpen && (
        <AlertModal isOpen={infoModal.isOpen} info={infoModal.text} onClose={closeInfoModal} />
      )}
    </div>
  );
};

SheetForm.propTypes = {
  classes: propTypes.object.isRequired,
  closeDrawer: propTypes.func.isRequired,
  openSnackbar: propTypes.func.isRequired,
  type: propTypes.string.isRequired,
  sheet: propTypes.object,
  // isDataLoading: propTypes.bool.isRequired,
  // projects: propTypes.array.isRequired,
};

export default withStyles(styles)(SheetForm);
